import * as React from "react";
import useEmblaCarousel, { type UseEmblaCarouselType } from "embla-carousel-react";
import { ArrowBack, ArrowBackIos, ArrowForward, ArrowForwardIos } from "@mui/icons-material";
import { Box, Button, IconButton } from "@mui/material";

type CarouselApi = UseEmblaCarouselType[1];
type UseCarouselParameters = Parameters<typeof useEmblaCarousel>;
type CarouselOptions = UseCarouselParameters[0];
type CarouselPlugin = UseCarouselParameters[1];

type CarouselProps = {
  opts?: CarouselOptions;
  plugins?: CarouselPlugin;
  orientation?: "horizontal" | "vertical";
  setApi?: (api: CarouselApi) => void;
};

type CarouselContextProps = {
  carouselRef: ReturnType<typeof useEmblaCarousel>[0];
  api: ReturnType<typeof useEmblaCarousel>[1];
  scrollPrev: () => void;
  scrollNext: () => void;
  canScrollPrev: boolean;
  canScrollNext: boolean;
} & CarouselProps;

const CarouselContext = React.createContext<CarouselContextProps | null>(null);

function useCarousel() {
  const context = React.useContext(CarouselContext);
  if (!context) throw new Error("useCarousel must be used within a <Carousel />");
  return context;
}

const Carousel = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement> & CarouselProps>(
  ({ orientation = "horizontal", opts, setApi, plugins, children, ...props }, ref) => {
    const [carouselRef, api] = useEmblaCarousel(
      { ...opts, axis: orientation === "horizontal" ? "x" : "y" },
      plugins
    );
    const [canScrollPrev, setCanScrollPrev] = React.useState(false);
    const [canScrollNext, setCanScrollNext] = React.useState(false);

    const onSelect = React.useCallback((api: CarouselApi) => {
      if (!api) return;
      setCanScrollPrev(api.canScrollPrev());
      setCanScrollNext(api.canScrollNext());
    }, []);

    const scrollPrev = React.useCallback(() => api?.scrollPrev(), [api]);
    const scrollNext = React.useCallback(() => api?.scrollNext(), [api]);

    const handleKeyDown = React.useCallback(
      (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.key === "ArrowLeft") {
          event.preventDefault();
          scrollPrev();
        } else if (event.key === "ArrowRight") {
          event.preventDefault();
          scrollNext();
        }
      },
      [scrollPrev, scrollNext]
    );

    React.useEffect(() => {
      if (!api || !setApi) return;
      setApi(api);
    }, [api, setApi]);

    React.useEffect(() => {
      if (!api) return;
      onSelect(api);
      api.on("reInit", onSelect);
      api.on("select", onSelect);
      return () => {
        api?.off("select", onSelect);
      };
    }, [api, onSelect]);

    return (
      <CarouselContext.Provider
        value={{
          carouselRef,
          api,
          opts,
          orientation: orientation || (opts?.axis === "y" ? "vertical" : "horizontal"),
          scrollPrev,
          scrollNext,
          canScrollPrev,
          canScrollNext,
        }}
      >
        <Box
          ref={ref}
          onKeyDownCapture={handleKeyDown}
          position="relative"
          sx={{ height: "100%" }}
          {...props}
        >
          {children}
        </Box>
      </CarouselContext.Provider>
    );
  }
);
Carousel.displayName = "Carousel";

const CarouselContent = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  (props, ref) => {
    const { carouselRef, orientation } = useCarousel();

    return (
      <Box ref={carouselRef} sx={{ overflow: "hidden", height: "100%" }}>
        <Box
          ref={ref}
          display="flex"
          flexDirection={orientation === "horizontal" ? "row" : "column"}
          sx={{ height: "100%" }}
          {...props}
        />
      </Box>
    );
  }
);
CarouselContent.displayName = "CarouselContent";

const CarouselItem = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  (props, ref) => {
    const { orientation } = useCarousel();

    return (
      <Box
        ref={ref}
        role="group"
        aria-roledescription="slide"
        flexShrink={0}
        flexGrow={0}
        flexBasis="100%"
        sx={{ height: "100%" }}
        {...props}
      />
    );
  }
);
CarouselItem.displayName = "CarouselItem";

const CarouselPrevious = React.forwardRef<HTMLButtonElement, React.ComponentProps<typeof IconButton>>(
  (props, ref) => {
    const { orientation, scrollPrev, canScrollPrev } = useCarousel();

    return (
      <IconButton
        ref={ref}
        onClick={scrollPrev}
        disabled={!canScrollPrev}
        sx={{
          position: "absolute",
          top: orientation === "horizontal" ? "50%" : orientation === "vertical" ? "-40px" : undefined,
          left: orientation === "horizontal" ? 0 : "50%",
          transform: orientation === "horizontal" ? "translateY(-50%)" : "translateX(-50%) rotate(90deg)",
          color: "white",
          opacity: 0.4,
          ":hover": {
            opacity: 1
          }
        }}
        {...props}
      >
        <ArrowBackIos fontSize="small" />
      </IconButton>
    );
  }
);
CarouselPrevious.displayName = "CarouselPrevious";

const CarouselNext = React.forwardRef<HTMLButtonElement, React.ComponentProps<typeof IconButton>>(
  (props, ref) => {
    const { orientation, scrollNext, canScrollNext } = useCarousel();

    return (
      <IconButton
        ref={ref}
        onClick={scrollNext}
        disabled={!canScrollNext}
        sx={{
          position: "absolute",
          top: orientation === "horizontal" ? "50%" : undefined,
          right: orientation === "horizontal" ? 0 : "50%",
          transform:
            orientation === "horizontal"
              ? "translateY(-50%)"
              : "translateX(-50%) rotate(90deg)",
          bottom: orientation === "vertical" ? "-40px" : undefined,
          color: "white",
          opacity: 0.4,
          ":hover": {
            opacity: 1
          }
        }}
        {...props}
      >
        <ArrowForwardIos fontSize="small" />
      </IconButton>
    );
  }
);
CarouselNext.displayName = "CarouselNext";

export { type CarouselApi, Carousel, CarouselContent, CarouselItem, CarouselPrevious, CarouselNext };
